/* PmosV3Extras — UI primitives for transparency + inline data entry + personalized lessons (iter446).
 *
 * Exports:
 *  - QuickLogDialog (single dialog, modes: sleep/mood/pain/weight/cycle)
 *  - QuickLogGrid (Bento grid of quick log icon buttons)
 *  - DriverExplainerPopover (transparent driver explainer)
 *  - SourceAttributionChip (small source + freshness chip)
 *  - ConfidenceIndicator (3-segment bar + tooltip)
 *  - PhenotypeLessonSheet (clickable lesson card → side sheet with AI personalization)
 *  - PmosAssessmentLink (CTA — embeds legacy assessment in modal route)
 *
 * Bulgarian-first; clean text without foreign loanwords.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Moon, Smile, Salad, Heart, Activity, Scale, ChevronRight,
  Database, Info, Sparkles, Loader2, BookOpen, X, Plus, Check,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiError } from "@/lib/formatApiError";

/* ─────────────── Quick Log ─────────────── */
const QUICK_LOG_MODES = [
  { key: "sleep",  icon: Moon,     label_bg: "Сън",          color: "indigo" },
  { key: "mood",   icon: Smile,    label_bg: "Настроение",   color: "amber"  },
  { key: "meal",   icon: Salad,    label_bg: "Хранене",      color: "emerald", goToRoute: "/dashboard/diary" },
  { key: "pain",   icon: Heart,    label_bg: "Болка",        color: "rose"   },
  { key: "cycle",  icon: Activity, label_bg: "Цикъл",        color: "pink"   },
  { key: "weight", icon: Scale,    label_bg: "Тегло",        color: "violet" },
];

export function QuickLogGrid({ onLogged }) {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [openMode, setOpenMode] = useState(null);
  const [status, setStatus] = useState({});
  const [showAll, setShowAll] = useState(false);
  const { API } = useAuth();

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/wh/quick-log/today-status`);
      setStatus(data);
    } catch { /* */ }
  }, [API]);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const handleLogged = () => {
    loadStatus();
    onLogged?.();
  };

  // iter455: Smart Requests — show only MISSING logs by default; "show all" toggle.
  const missing = QUICK_LOG_MODES.filter((m) => !status[`${m.key}_logged`]);
  const allDone = missing.length === 0;
  const visible = showAll ? QUICK_LOG_MODES : missing;

  if (allDone && !showAll) {
    return (
      <div data-testid="quick-log-grid">
        <div className={`p-3 rounded-2xl flex items-center gap-3 ${isDark ? "bg-emerald-950/30" : "bg-emerald-50"}`} data-testid="quick-log-all-done">
          <div className={`w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center`}>
            <Check className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <p className={`text-sm font-bold ${isDark ? "text-emerald-100" : "text-emerald-900"}`}>
              Всичко важно е записано за днес — браво!
            </p>
            <p className={`text-[11px] ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
              Утре те чакаме отново. Програмата ще се настрои спрямо новите данни.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className={`text-[11px] underline ${isDark ? "text-emerald-300" : "text-emerald-700"}`}
            data-testid="quick-log-show-all"
          >
            Запиши още
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quick-log-grid">
      <div className="flex items-center justify-between mb-2">
        <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
          {showAll || allDone ? "Бърз запис за днес" : "За по-точни препоръки добави:"}
        </p>
        {showAll && missing.length === 0 && (
          <button type="button" onClick={() => setShowAll(false)} className="text-[10px] underline opacity-70" data-testid="quick-log-collapse">
            Свий
          </button>
        )}
      </div>
      <div className={`grid gap-1.5 ${visible.length <= 3 ? "grid-cols-3" : "grid-cols-3 sm:grid-cols-6"}`}>
        {visible.map((m) => {
          const Icon = m.icon;
          const isDone = status[`${m.key}_logged`];
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => {
                if (m.goToRoute) navigate(m.goToRoute);
                else setOpenMode(m.key);
              }}
              className={`p-3 rounded-2xl border transition flex flex-col items-center gap-1 ${
                isDone
                  ? isDark ? "bg-emerald-950/30 border-emerald-700" : "bg-emerald-50 border-emerald-200"
                  : isDark ? "bg-slate-900/40 border-slate-800 hover:border-pink-500" : "bg-white border-[#E5E2DA] hover:border-pink-400"
              }`}
              data-testid={`quick-log-${m.key}`}
            >
              <Icon className={`w-4 h-4 ${isDone ? "text-emerald-500" : isDark ? "text-pink-300" : "text-pink-600"}`} />
              <span className={`text-[10px] font-bold ${isDark ? "text-slate-200" : "text-[#2A342B]"}`}>{m.label_bg}</span>
              {isDone && <span className="text-[9px] text-emerald-500">✓</span>}
            </button>
          );
        })}
      </div>
      {!showAll && missing.length < QUICK_LOG_MODES.length && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className={`text-[10px] mt-2 underline opacity-70 ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}
          data-testid="quick-log-show-all"
        >
          Виж всички ({QUICK_LOG_MODES.length - missing.length} вече попълнени)
        </button>
      )}
      <QuickLogDialog mode={openMode} onClose={() => setOpenMode(null)} onLogged={handleLogged} />
    </div>
  );
}

export function QuickLogDialog({ mode, onClose, onLogged }) {
  const { API } = useAuth();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (mode === "cycle") {
      setForm({ start_date: new Date().toISOString().slice(0, 10) });
    } else {
      setForm({});
    }
  }, [mode]);

  if (!mode) return null;

  const submit = async () => {
    setBusy(true);
    try {
      let endpoint = `${API}/wh/quick-log/${mode}`;
      const body = { ...form };
      if (mode === "cycle") {
        endpoint = `${API}/wh/quick-log/cycle-start`;
        if (!body.start_date) body.start_date = new Date().toISOString().slice(0, 10);
      }
      const { data: resp } = await axios.post(endpoint, body);
      const baseMsg = "Записано";
      const extraMsg = resp?.saved_to_health_intelligence ? " · ✓ Запазено и в Health Intelligence" : "";
      toast.success(baseMsg + extraMsg);
      // iter451: notify all listeners (DriverExplainerPopover, etc.) to refetch
      try { window.dispatchEvent(new CustomEvent("quick-log-saved", { detail: { mode } })); } catch { /* */ }
      onLogged?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e, "Грешка при запис"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!mode} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700" data-testid={`quick-log-dialog-${mode}`}>
        <DialogHeader>
          <DialogTitle>Бърз запис: {QUICK_LOG_MODES.find(m => m.key === mode)?.label_bg}</DialogTitle>
          <DialogDescription>Записът се добавя за днес. Можете да го редактирате после.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {mode === "sleep" && (
            <>
              <div>
                <Label htmlFor="qlog-hours">Часове сън</Label>
                <Input id="qlog-hours" type="number" step="0.5" min="0" max="16"
                  value={form.hours || ""} onChange={(e) => setForm({ ...form, hours: parseFloat(e.target.value) })}
                  data-testid="quick-log-sleep-hours" />
              </div>
              <div>
                <Label htmlFor="qlog-q">Качество (1–5)</Label>
                <Input id="qlog-q" type="number" min="1" max="5"
                  value={form.quality || ""} onChange={(e) => setForm({ ...form, quality: parseInt(e.target.value) })}
                  data-testid="quick-log-sleep-quality" />
              </div>
            </>
          )}

          {mode === "mood" && (
            <>
              {[
                { k: "mood", lab: "Настроение" },
                { k: "stress", lab: "Стрес" },
                { k: "energy", lab: "Енергия" },
                { k: "anxiety", lab: "Тревожност" },
              ].map(({ k, lab }) => (
                <div key={k}>
                  <Label htmlFor={`qlog-${k}`}>{lab} (1–10)</Label>
                  <Input id={`qlog-${k}`} type="number" min="1" max="10"
                    value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: parseInt(e.target.value) })}
                    data-testid={`quick-log-mood-${k}`} />
                </div>
              ))}
            </>
          )}

          {mode === "pain" && (
            <>
              <div>
                <Label htmlFor="qlog-sev">Тежест (0–10)</Label>
                <Input id="qlog-sev" type="number" min="0" max="10"
                  value={form.severity ?? ""} onChange={(e) => setForm({ ...form, severity: parseInt(e.target.value) })}
                  data-testid="quick-log-pain-severity" />
              </div>
              <div>
                <Label htmlFor="qlog-loc">Локализация</Label>
                <Input id="qlog-loc" type="text" placeholder="напр. долна част на корема"
                  value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })}
                  data-testid="quick-log-pain-location" />
              </div>
            </>
          )}

          {mode === "weight" && (
            <>
              <div>
                <Label htmlFor="qlog-w">Тегло (кг)</Label>
                <Input id="qlog-w" type="number" step="0.1"
                  value={form.weight_kg || ""} onChange={(e) => setForm({ ...form, weight_kg: parseFloat(e.target.value) })}
                  data-testid="quick-log-weight-kg" />
              </div>
              <div>
                <Label htmlFor="qlog-waist">Талия (см) — по желание</Label>
                <Input id="qlog-waist" type="number" step="0.5"
                  value={form.waist_cm || ""} onChange={(e) => setForm({ ...form, waist_cm: parseFloat(e.target.value) })}
                  data-testid="quick-log-waist-cm" />
              </div>
            </>
          )}

          {mode === "cycle" && (
            <>
              <div>
                <Label htmlFor="qlog-cstart">Начало (дата)</Label>
                <Input id="qlog-cstart" type="date"
                  value={form.start_date || new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                  data-testid="quick-log-cycle-start" />
              </div>
              <div>
                <Label htmlFor="qlog-clen">Дължина (дни) — по желание</Label>
                <Input id="qlog-clen" type="number" min="14" max="90"
                  value={form.length_days || ""} onChange={(e) => setForm({ ...form, length_days: parseInt(e.target.value) })}
                  data-testid="quick-log-cycle-length" />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="quick-log-cancel">Затвори</Button>
          <Button onClick={submit} disabled={busy} className="bg-pink-500 hover:bg-pink-600 text-white" data-testid={`quick-log-submit-${mode}`}>
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
            Запази
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────── DriverExplainerPopover ─────────────── */
const SOURCE_TO_QUICKLOG = {
  mood_logs: "mood",
  sleep_logs: "sleep",
  endo_pain_entries: "pain",
  body_composition: "weight",
  pmos_cycle_history: "cycle",
  meal_log: "meal",
  activity_log: null,
  pmos_lab_history: null,
  pmos_symptoms: null,
};

export function DriverExplainerPopover({ driverKey, children }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [quickLogMode, setQuickLogMode] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    if (!driverKey) return;
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/wh/pmos-program/driver-explainer?driver=${driverKey}`);
      setData(d);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [API, driverKey]);

  // iter451: always refetch when popover opens (no stale cache); also refresh
  // automatically when a quick-log was saved anywhere on the page.
  useEffect(() => {
    const handler = () => { if (driverKey) load(); };
    window.addEventListener("quick-log-saved", handler);
    return () => window.removeEventListener("quick-log-saved", handler);
  }, [load, driverKey]);

  return (
    <>
      <Popover onOpenChange={(o) => { if (o) load(); }}>
        <PopoverTrigger asChild>
          <button type="button" className="inline-flex items-center gap-1 cursor-pointer" data-testid={`driver-explainer-trigger-${driverKey}`}>
            {children}
            <Info className="w-3.5 h-3.5 opacity-60" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700" data-testid={`driver-explainer-popover-${driverKey}`}>
          {loading || !data ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-pink-500" />
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
                  Защо точно този фактор?
                </p>
                <p className="text-sm font-bold mt-0.5">{data.label_bg}</p>
                <p className="text-xs mt-0.5">Принос: {data.contribution_pct}% · Стойност: {data.value}/100</p>
              </div>

              <ConfidenceIndicator level={data.confidence} levelLabel={data.confidence_bg} />

              <div>
                <p className="text-[10px] uppercase tracking-wider font-bold mb-1">Източници на данни</p>
                <ul className="space-y-1">
                  {data.data_sources.map((s, i) => {
                    const ql = SOURCE_TO_QUICKLOG[s.src];
                    return (
                      <li key={i} className="text-xs">
                        <div className="flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1"><Database className="w-3 h-3" /> {s.label_bg}</span>
                          <span className="text-[10px] opacity-70">{s.n_recent_30d} запис(а) / 30д</span>
                        </div>
                        {ql && (
                          <button type="button"
                            onClick={() => { if (ql === "meal") navigate("/dashboard/diary"); else setQuickLogMode(ql); }}
                            className="mt-1 inline-flex items-center gap-1 text-[10px] font-bold text-pink-600 hover:text-pink-700"
                            data-testid={`explainer-quicklog-${ql}`}>
                            <Plus className="w-3 h-3" /> Добави запис сега
                          </button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {data.how_to_improve_confidence?.length > 0 && (
                <div className={`p-2 rounded-lg ${isDark ? "bg-amber-950/30 text-amber-100" : "bg-amber-50 text-amber-900"} text-xs`}>
                  <p className="font-bold mb-1">Как да повишите точността:</p>
                  <ul className="space-y-0.5">
                    {data.how_to_improve_confidence.map((i, k) => (
                      <li key={k}>• {i.bg}</li>
                    ))}
                  </ul>
                </div>
              )}

              <p className="text-[10px] italic opacity-70">{data.disclaimer_bg}</p>
            </div>
          )}
        </PopoverContent>
      </Popover>
      <QuickLogDialog mode={quickLogMode} onClose={() => setQuickLogMode(null)} onLogged={() => { setQuickLogMode(null); load(); }} />
    </>
  );
}

/* ─────────────── SourceAttributionChip ─────────────── */
export function SourceAttributionChip({ source, freshness, isDark }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] mt-1 ${isDark ? "text-slate-500" : "text-[#8F9990]"}`} data-testid="source-attribution">
      <Database className="w-2.5 h-2.5" />
      <span>Източник: {source}{freshness ? ` · ${freshness}` : ""}</span>
    </span>
  );
}

/* ─────────────── ConfidenceIndicator ─────────────── */
export function ConfidenceIndicator({ level, levelLabel, hint }) {
  const LEVELS = { low: 1, moderate: 2, high: 3 };
  const filled = LEVELS[level] || 0;
  const colors = { low: "bg-rose-500", moderate: "bg-amber-500", high: "bg-emerald-500" };
  return (
    <div className="inline-flex items-center gap-2" data-testid="confidence-indicator">
      <div className="flex gap-0.5">
        {[1, 2, 3].map((i) => (
          <div key={i} className={`w-4 h-1.5 rounded-full ${i <= filled ? colors[level] : "bg-slate-300 dark:bg-slate-700"}`} />
        ))}
      </div>
      <span className="text-[10px] font-bold">Доверие: {levelLabel || level}</span>
      {hint && <Info className="w-3 h-3 opacity-60" title={hint} />}
    </div>
  );
}

/* ─────────────── PhenotypeLessonSheet ─────────────── */
export function PhenotypeLessonSheet({ lessonId, lessonTitle, lessonMin, isDark, bg = true }) {
  const { API } = useAuth();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [personalizing, setPersonalizing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/wh/pmos-program/lesson/${lessonId}`);
      setData(d);
    } finally { setLoading(false); }
  }, [API, lessonId]);

  const personalize = async () => {
    setPersonalizing(true);
    try {
      const { data: d } = await axios.post(`${API}/wh/pmos-program/lesson/${lessonId}/personalize`, { force: false });
      setData(d);
      toast.success("Урокът е персонализиран");
    } catch (e) {
      toast.error(formatApiError(e, "Грешка при персонализиране"));
    } finally { setPersonalizing(false); }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); if (!data) load(); }}
        className={`text-left p-3 rounded-xl w-full ${isDark ? "bg-slate-900/40 hover:bg-slate-900/60" : "bg-[#F5F3ED] hover:bg-[#EBE6DA]"} transition`}
        data-testid={`lesson-trigger-${lessonId}`}
      >
        <p className={`text-[10px] font-bold uppercase tracking-wider ${isDark ? "text-pink-300" : "text-pink-700"}`}>
          <BookOpen className="w-3 h-3 inline mr-1" />{lessonMin} мин
        </p>
        <p className={`text-sm font-bold flex items-center justify-between ${isDark ? "text-white" : "text-[#2A342B]"}`}>
          <span>{lessonTitle}</span>
          <ChevronRight className="w-3.5 h-3.5 opacity-60" />
        </p>
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent className="overflow-y-auto sm:max-w-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100" data-testid={`lesson-sheet-${lessonId}`}>
          <SheetHeader>
            <SheetTitle>{lessonTitle}</SheetTitle>
            <SheetDescription>{lessonMin} минути · образователно съдържание</SheetDescription>
          </SheetHeader>

          <div className="mt-4 space-y-3">
            {loading || !data ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
              </div>
            ) : (
              <>
                <div className={`text-sm whitespace-pre-line leading-relaxed ${isDark ? "text-slate-200" : "text-[#2A342B]"}`} data-testid="lesson-content">
                  {data.content_bg}
                </div>

                <div className={`p-3 rounded-xl ${isDark ? "bg-violet-950/30" : "bg-violet-50"}`}>
                  <p className="text-xs">
                    {data.is_personalized
                      ? "Този урок е персонализиран спрямо Вашия фенотип и изследвания."
                      : data.context?.phenotype
                        ? "Можете да персонализирате урока спрямо Вашия фенотип и лабораторни данни."
                        : "Попълнете PMOS оценка и изследвания, за да получите персонализиран урок."}
                  </p>
                  {!data.is_personalized && data.can_personalize && (
                    <Button onClick={personalize} disabled={personalizing} size="sm"
                      className="mt-2 bg-violet-500 hover:bg-violet-600 text-white"
                      data-testid={`lesson-personalize-${lessonId}`}>
                      {personalizing ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                      Персонализирай
                    </Button>
                  )}
                </div>
                {data.generated_at && (
                  <p className="text-[10px] opacity-60">Генерирано: {new Date(data.generated_at).toLocaleString("bg-BG")}</p>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

/* ─────────────── PmosAssessmentLink — embeds legacy PmosAssessmentForm in a Dialog ─────────────── */
export function PmosAssessmentLink({ children, className, onSaved }) {
  const [open, setOpen] = useState(false);
  const [Form, setForm] = useState(null);

  const openDialog = async () => {
    if (!Form) {
      const mod = await import("@/components/pmos/PmosAssessmentForm");
      setForm(() => mod.default);
    }
    setOpen(true);
  };

  const handleSaved = () => {
    setOpen(false);
    // Notify parent so it can refetch data; also broadcast a window event so
    // any listening component (PmosProgramHome etc.) can refresh.
    try { window.dispatchEvent(new CustomEvent("pmos-assessment-saved")); } catch { /* */ }
    if (onSaved) onSaved();
  };

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        className={className || "inline-flex items-center gap-1 text-xs text-pink-600 hover:text-pink-700 underline"}
        data-testid="pmos-assessment-link"
      >
        {children || "Към PMOS оценка"}
        <ChevronRight className="w-3 h-3" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700" data-testid="pmos-assessment-dialog">
          <DialogHeader>
            <DialogTitle>PMOS оценка</DialogTitle>
            <DialogDescription>Попълнете данните, за да получите Вашия профил, фактори и персонализирани препоръки.</DialogDescription>
          </DialogHeader>
          {Form ? (
            <Form bg={true} onSaved={handleSaved} />
          ) : (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-pink-500" />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─────────────── ScoreBreakdownPopover ─────────────── */
export function ScoreBreakdownPopover({ metric, value, children }) {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/wh/pmos-program/score-breakdown?metric=${metric}`);
      setData(d);
    } finally { setLoading(false); }
  }, [API, metric]);

  return (
    <Popover onOpenChange={(o) => { if (o && !data) load(); }}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 cursor-pointer" data-testid={`score-breakdown-trigger-${metric}`}>
          {children}
          <Info className="w-3 h-3 opacity-60" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4 bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 border-slate-200 dark:border-slate-700" data-testid={`score-breakdown-popover-${metric}`}>
        {loading || !data ? (
          <Loader2 className="w-4 h-4 animate-spin text-pink-500 mx-auto" />
        ) : (
          <div className="space-y-2">
            <p className="text-sm font-bold">{data.label_bg} · {data.actual_value}</p>
            <p className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
              Как се изчислява
            </p>
            <ul className="space-y-1.5">
              {data.components.map((c) => (
                <li key={c.key}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold">{c.label_bg}</span>
                    <span>{Math.round(c.weight * 100)}% · {c.n_records} зап.</span>
                  </div>
                  <div className={`h-1 rounded-full mt-0.5 ${isDark ? "bg-slate-800" : "bg-slate-200"}`}>
                    <div className="h-full rounded-full bg-gradient-to-r from-pink-500 to-fuchsia-500"
                         style={{ width: `${c.weight * 100}%` }} />
                  </div>
                  {!c.data_available && (
                    <p className="text-[9px] text-amber-500 mt-0.5">⚠ Няма данни — добавете запис.</p>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-[10px] italic opacity-70">{data.disclaimer_bg}</p>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
